import { useQuery } from '@tanstack/react-query';
import { dbClient, normalizeRpcResponse } from '@/services/db-client';

export interface WebFunnelDefinition {
    id: string;
    label: string;
    kind: 'event' | 'funnel_step';
    funnel_key: string | null;
    definition: { steps?: unknown[] };
    valid_from: string;
    valid_to: string | null;
    is_active: boolean;
}

export interface WebFunnelStepMetric {
    id: string;
    label: string;
    active_users: number;
    event_count: number;
    pct_of_first: number | null;
    optional?: boolean;
}

export interface WebFunnelTransition {
    from_step: string;
    to_step: string;
    active_users: number;
    event_count: number;
}

export interface WebFunnelReport {
    definition_id: string;
    label: string;
    kind: string;
    funnel_key: string | null;
    days: number;
    data_through: string | null;
    steps: WebFunnelStepMetric[];
    transitions: WebFunnelTransition[];
    note: string;
}

export function useWebFunnelDefinitions() {
    return useQuery<WebFunnelDefinition[]>({
        queryKey: ['admin', 'web-funnel-definitions'],
        queryFn: async () => {
            const { data, error } = await dbClient.rpc<WebFunnelDefinition[]>('list_web_funnel_definitions');
            if (error) throw new Error(error.message);
            const rows = normalizeRpcResponse(data);
            const first = rows[0];
            const parsed = first && typeof first === 'object' && 'list_web_funnel_definitions' in first
                ? (first as { list_web_funnel_definitions: WebFunnelDefinition[] }).list_web_funnel_definitions
                : first;
            return Array.isArray(parsed) ? parsed : [];
        },
    });
}

export function useWebFunnelReport(definitionId: string | null, days = 28) {
    return useQuery<WebFunnelReport>({
        queryKey: ['admin', 'web-funnel-report', definitionId, days],
        enabled: Boolean(definitionId),
        queryFn: async () => {
            const { data, error } = await dbClient.rpc<WebFunnelReport>('get_web_funnel_report', {
                p_definition_id: definitionId,
                p_days: days,
            });
            if (error) throw new Error(error.message);
            const rows = normalizeRpcResponse(data);
            const first = rows[0];
            const parsed = first && typeof first === 'object' && 'get_web_funnel_report' in first
                ? (first as { get_web_funnel_report: WebFunnelReport }).get_web_funnel_report
                : (first as WebFunnelReport);
            if (!parsed || !Array.isArray(parsed.steps)) {
                throw new Error('Respuesta inválida de get_web_funnel_report');
            }
            return parsed;
        },
    });
}
